import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  createCreditInvoice,
  type CreditLineInput,
  type CreditStepError,
  type InvoiceDetail,
  type InvoiceLineItem,
} from '../../lib/invoices';
import { formatCurrency } from '../../lib/format';

interface CreditInvoiceDialogProps {
  isOpen: boolean;
  invoice: InvoiceDetail['invoice'];
  lineItems: InvoiceLineItem[];
  onClose: () => void;
  /** Na succesvolle credit (ook bij partial failure van refund/send) — refetch. */
  onSuccess: (creditNaam: string, stepErrors: CreditStepError[]) => void;
}

interface FreeLine {
  omschrijving: string;
  bedrag: string; // invoer, komma toegestaan
  btwPct: 0 | 9 | 21;
}

function parseBedrag(s: string): number {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

function lineLabel(li: InvoiceLineItem): string {
  return li.customName || li.productName || 'Regel';
}

const STAP_LABEL: Record<string, string> = {
  refund: 'Terugstorting',
  send: 'Versturen',
  release: 'Order weer factureerbaar maken',
};

const STAP_HERSTEL: Record<string, string> = {
  refund: 'herhaal via de orderpagina (Refunds).',
  send: 'verstuur opnieuw vanaf de creditfactuur-pagina.',
  release: 'de creditfactuur staat; geef de order los vrij vanaf de orderpagina.',
};

export function CreditInvoiceDialog({
  isOpen,
  invoice,
  lineItems,
  onClose,
  onSuccess,
}: CreditInvoiceDialogProps) {
  // Default: hele factuur (alle regels aangevinkt).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(lineItems.map((li) => li.id))
  );
  const [freeLines, setFreeLines] = useState<FreeLine[]>([]);
  const [reden, setReden] = useState('');
  const [toelichting, setToelichting] = useState('');
  const [refundEnabled, setRefundEnabled] = useState<boolean | null>(null); // null = auto
  const [refundMethode, setRefundMethode] = useState<'Mollie' | 'Bank' | null>(null);
  const [refundBedragInput, setRefundBedragInput] = useState<string | null>(null); // null = auto
  const [sendToCustomer, setSendToCustomer] = useState(true);
  const [releaseWerkOrder, setReleaseWerkOrder] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stepErrors, setStepErrors] = useState<CreditStepError[] | null>(null);

  // Reeds gecrediteerd op dit origineel (credits zijn negatief in de DB).
  const alreadyCredited = useMemo(
    () =>
      invoice.credit.creditInvoices.reduce((sum, c) => sum + Math.abs(c.totalInclBtw), 0),
    [invoice.credit.creditInvoices]
  );

  const creditTotal = useMemo(() => {
    let total = 0;
    for (const li of lineItems) {
      if (selectedIds.has(li.id)) total += li.totalInclBtw;
    }
    for (const fl of freeLines) total += parseBedrag(fl.bedrag);
    return Math.round(total * 100) / 100;
  }, [lineItems, selectedIds, freeLines]);

  // Rekenregel (spiegel van InvoiceCreditService): verreken eerst tegen
  // openstaand origineel, restant is terug te betalen.
  const openAmount = Math.max(invoice.status.openAmount, 0);
  const verrekend = Math.min(creditTotal, openAmount);
  const terugTeBetalen = Math.round((creditTotal - verrekend) * 100) / 100;
  const nieuwOpenstaand = Math.round((openAmount - verrekend) * 100) / 100;

  const exceedsInvoice = alreadyCredited + creditTotal > invoice.totals.totalInclBtw + 0.01;

  // Vrijgeven kan alleen bij een werkorder-factuur die na deze credit volledig
  // gecrediteerd is; een deels gecrediteerde factuur hoort te blijven staan.
  const wordtVolledigGecrediteerd =
    alreadyCredited + creditTotal >= invoice.totals.totalInclBtw - 0.01;
  const kanVrijgeven = !!invoice.workOrderId && wordtVolledigGecrediteerd;

  // Slimme defaults: terugstorting aan zodra er iets terug te betalen is,
  // Mollie als er een payment is, anders Bank.
  const effectiveRefundEnabled = refundEnabled ?? terugTeBetalen > 0;
  // Gate op refundPaymentId (factuur met werkorder-fallback), want dat is de
  // bron die de refund-service gebruikt. Gate op invoice.mollie.paymentId zette
  // Mollie grijs bij elke werkorder-factuur, ook als de klant via iDEAL betaalde.
  const mollieBeschikbaar = !!invoice.mollie.refundPaymentId;
  const defaultMethode: 'Mollie' | 'Bank' = mollieBeschikbaar ? 'Mollie' : 'Bank';
  const effectiveMethode = refundMethode ?? defaultMethode;
  const effectiveRefundBedrag =
    refundBedragInput !== null ? parseBedrag(refundBedragInput) : terugTeBetalen;

  if (!isOpen) return null;

  const toggleLine = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addFreeLine = () =>
    setFreeLines((prev) => [...prev, { omschrijving: '', bedrag: '', btwPct: 21 }]);
  const updateFreeLine = (idx: number, patch: Partial<FreeLine>) =>
    setFreeLines((prev) => prev.map((fl, i) => (i === idx ? { ...fl, ...patch } : fl)));
  const removeFreeLine = (idx: number) =>
    setFreeLines((prev) => prev.filter((_, i) => i !== idx));

  const freeLinesValid = freeLines.every(
    (fl) => fl.omschrijving.trim().length > 0 && parseBedrag(fl.bedrag) > 0
  );
  const canSubmit =
    creditTotal > 0 &&
    reden.trim().length > 0 &&
    freeLinesValid &&
    !exceedsInvoice &&
    (!effectiveRefundEnabled || effectiveRefundBedrag > 0);

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    setStepErrors(null);

    const lines: CreditLineInput[] = [
      ...lineItems.filter((li) => selectedIds.has(li.id)).map((li) => ({ lineItemId: li.id })),
      ...freeLines.map((fl) => ({
        omschrijving: fl.omschrijving.trim(),
        bedragInclBtw: parseBedrag(fl.bedrag),
        btwPct: fl.btwPct,
      })),
    ];

    const result = await createCreditInvoice({
      invoiceId: invoice.id,
      werkOrderId: invoice.workOrderId || undefined,
      lines,
      reden: reden.trim(),
      toelichting: toelichting.trim() || undefined,
      refund:
        effectiveRefundEnabled && effectiveRefundBedrag > 0
          ? { methode: effectiveMethode, bedrag: effectiveRefundBedrag }
          : undefined,
      send: sendToCustomer,
      releaseWerkOrder: kanVrijgeven && releaseWerkOrder,
    });

    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (result.stepErrors.length > 0) {
      // Creditfactuur staat — refund of versturen faalde. Toon wat er misging;
      // kantoor kan die stap los herhalen (refund via orderpagina, versturen
      // via de creditfactuur-pagina).
      setStepErrors(result.stepErrors);
      onSuccess(result.creditInvoiceNaam, result.stepErrors);
      return;
    }
    onSuccess(result.creditInvoiceNaam, []);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-slate-900">Factuur crediteren</h2>
          <p className="text-xs text-slate-500 mt-1">
            Factuur {invoice.name}
            {alreadyCredited > 0 && (
              <span className="ml-2 text-amber-600">
                — al eerder gecrediteerd: {formatCurrency(alreadyCredited)}
              </span>
            )}
          </p>
        </div>

        <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
          {/* Stap 1: wat crediteren */}
          <fieldset>
            <legend className="text-sm font-medium text-slate-700 mb-2">
              1. Wat wil je crediteren?
            </legend>
            <div className="space-y-1.5">
              {lineItems.map((li) => (
                <label
                  key={li.id}
                  className={clsx(
                    'flex items-center gap-2 p-2 border rounded cursor-pointer text-sm',
                    selectedIds.has(li.id)
                      ? 'border-indigo-400 bg-indigo-50'
                      : 'border-slate-200 hover:bg-slate-50',
                    li.parentLineItemId && 'ml-5'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(li.id)}
                    onChange={() => toggleLine(li.id)}
                    disabled={submitting}
                  />
                  <span className="flex-1 truncate text-slate-900">{lineLabel(li)}</span>
                  <span className="text-slate-600 tabular-nums">
                    {formatCurrency(li.totalInclBtw)}
                  </span>
                </label>
              ))}
            </div>

            {freeLines.map((fl, idx) => (
              <div key={idx} className="mt-2 flex items-center gap-2">
                <input
                  type="text"
                  value={fl.omschrijving}
                  onChange={(e) => updateFreeLine(idx, { omschrijving: e.target.value })}
                  placeholder="Omschrijving (bv. Coulance)"
                  disabled={submitting}
                  className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={fl.bedrag}
                  onChange={(e) => updateFreeLine(idx, { bedrag: e.target.value })}
                  placeholder="€ incl."
                  disabled={submitting}
                  className="w-24 border border-slate-300 rounded px-2 py-1.5 text-sm text-right"
                />
                <select
                  value={fl.btwPct}
                  onChange={(e) =>
                    updateFreeLine(idx, { btwPct: Number(e.target.value) as 0 | 9 | 21 })
                  }
                  disabled={submitting}
                  className="border border-slate-300 rounded px-1.5 py-1.5 text-sm bg-white"
                >
                  <option value={21}>21%</option>
                  <option value={9}>9%</option>
                  <option value={0}>0%</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeFreeLine(idx)}
                  disabled={submitting}
                  className="text-slate-400 hover:text-red-600 text-sm px-1"
                  aria-label="Vrije regel verwijderen"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addFreeLine}
              disabled={submitting}
              className="mt-2 text-xs text-indigo-600 hover:underline"
            >
              + Vrij bedrag toevoegen
            </button>
          </fieldset>

          {/* Stap 2: terugstorten */}
          <fieldset>
            <legend className="text-sm font-medium text-slate-700 mb-2">2. Geld terugstorten?</legend>
            {terugTeBetalen > 0 ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={effectiveRefundEnabled}
                    onChange={(e) => setRefundEnabled(e.target.checked)}
                    disabled={submitting}
                  />
                  <span>
                    Stort <strong>{formatCurrency(effectiveRefundBedrag)}</strong> terug aan de
                    klant
                  </span>
                </label>
                {effectiveRefundEnabled && (
                  <div className="pl-6 space-y-1">
                    <div className="flex items-center gap-3">
                      <select
                        value={effectiveMethode}
                        onChange={(e) => setRefundMethode(e.target.value as 'Mollie' | 'Bank')}
                        disabled={submitting}
                        className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                      >
                        <option value="Mollie" disabled={!mollieBeschikbaar}>
                          Mollie (automatisch)
                        </option>
                        <option value="Bank">Bank (handmatige overboeking)</option>
                      </select>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={refundBedragInput ?? String(terugTeBetalen).replace('.', ',')}
                        onChange={(e) => setRefundBedragInput(e.target.value)}
                        disabled={submitting}
                        className="w-24 border border-slate-300 rounded px-2 py-1.5 text-sm text-right"
                      />
                    </div>
                    {!mollieBeschikbaar && (
                      <p className="text-xs text-slate-500">
                        Geen Mollie-payment op deze factuur of werkorder, dus automatisch
                        terugstorten kan hier niet.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                N.v.t. — het volledige creditbedrag wordt verrekend met het openstaande bedrag.
              </p>
            )}
          </fieldset>

          {/* Stap 3: reden + versturen */}
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-slate-700 mb-2">3. Afronden</legend>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">
                Reden <span className="text-slate-400 font-normal">(komt op de creditfactuur)</span>
              </span>
              <input
                type="text"
                value={reden}
                onChange={(e) => setReden(e.target.value)}
                maxLength={200}
                disabled={submitting}
                placeholder="bv. 'Annulering na ophalen' of 'Coulance na klacht'"
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">
                Interne toelichting (optioneel)
              </span>
              <textarea
                value={toelichting}
                onChange={(e) => setToelichting(e.target.value)}
                rows={2}
                maxLength={1024}
                disabled={submitting}
                placeholder="Niet zichtbaar voor klant"
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendToCustomer}
                onChange={(e) => setSendToCustomer(e.target.checked)}
                disabled={submitting}
              />
              <span>
                Creditfactuur e-mailen naar klant
                {invoice.email && <span className="text-slate-400"> ({invoice.email})</span>}
              </span>
            </label>
            {kanVrijgeven && (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={releaseWerkOrder}
                  onChange={(e) => setReleaseWerkOrder(e.target.checked)}
                  disabled={submitting}
                  className="mt-0.5"
                />
                <span>
                  Order weer factureerbaar maken
                  <span className="block text-xs text-slate-500">
                    Aanvinken als er nog een nieuwe factuur uit moet, bijvoorbeeld na een
                    correctie van de maten of het product. Laat uit bij een annulering.
                  </span>
                </span>
              </label>
            )}
          </fieldset>

          {/* Preview */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-600">Creditbedrag</span>
              <span className="font-medium tabular-nums">{formatCurrency(creditTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Verrekend met openstaand</span>
              <span className="tabular-nums">− {formatCurrency(verrekend)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Terug te betalen aan klant</span>
              <span className="tabular-nums">{formatCurrency(terugTeBetalen)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
              <span className="text-slate-600">Openstaand origineel na crediteren</span>
              <span className="font-medium tabular-nums">{formatCurrency(nieuwOpenstaand)}</span>
            </div>
            <p className="text-[11px] text-slate-400 pt-1">
              Vrije bedragen kunnen door BTW-afronding per regel een cent afwijken; het
              definitieve bedrag staat op de creditfactuur.
            </p>
          </div>

          {exceedsInvoice && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              Totaal gecrediteerd zou het factuurtotaal ({formatCurrency(invoice.totals.totalInclBtw)})
              overschrijden.
            </p>
          )}
          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
          {stepErrors && stepErrors.length > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-1">
              <p className="font-medium">Creditfactuur is aangemaakt, maar:</p>
              {stepErrors.map((se, i) => (
                <p key={i}>
                  • {STAP_LABEL[se.step] ?? se.step} mislukt
                  {se.message ? `: ${se.message}` : ''}
                  {` — ${STAP_HERSTEL[se.step] ?? 'probeer de stap los opnieuw.'}`}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          {!stepErrors && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded',
                submitting || !canSubmit
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              )}
            >
              {submitting
                ? 'Bezig…'
                : effectiveRefundEnabled && terugTeBetalen > 0
                  ? `Crediteren + ${formatCurrency(effectiveRefundBedrag)} terugstorten`
                  : 'Crediteren'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
